import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { ArrowBigUp, ArrowBigDown, MessageCircle, Send, User, Heart, MessageSquare } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useAuthStore } from '@/stores/authStore'
import api from '@/api/client'
import { toast } from '@/components/ui/toaster'
import { formatDistanceToNow } from '@/lib/utils'

export default function ForumTopic() {
  const { id } = useParams()
  const { isAuthenticated } = useAuthStore()
  const queryClient = useQueryClient()
  const [comment, setComment] = useState('')
  const [replyingTo, setReplyingTo] = useState<string | null>(null)
  const [replyText, setReplyText] = useState('')
  const { t } = useTranslation()

  const { data: topic } = useQuery({
    queryKey: ['forum-topic', id],
    queryFn: () => api.get(`/forum/topics/${id}`).then(r => r.data)
  })

  const voteMutation = useMutation({
    mutationFn: (vote: string) => api.put(`/forum/topics/${id}/vote`, { vote }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['forum-topic', id] }),
    onError: (err: any) => toast({ title: err.response?.data?.error || t('common.error'), variant: 'destructive' })
  })

  const commentMutation = useMutation({
    mutationFn: () => api.post(`/forum/topics/${id}/comments`, { content: comment }),
    onSuccess: () => {
      setComment('')
      queryClient.invalidateQueries({ queryKey: ['forum-topic', id] })
      toast({ title: t('forum.commentAdded') })
    },
    onError: (err: any) => toast({ title: err.response?.data?.error || t('common.error'), variant: 'destructive' })
  })

  const likeMutation = useMutation({
    mutationFn: (commentId: string) => api.put(`/forum/comments/${commentId}/like`).then(r => r.data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['forum-topic', id] }),
    onError: (err: any) => toast({ title: err.response?.data?.error || t('common.error'), variant: 'destructive' })
  })

  const replyMutation = useMutation({
    mutationFn: ({ commentId, content }: { commentId: string; content: string }) =>
      api.post(`/forum/comments/${commentId}/reply`, { content }).then(r => r.data),
    onSuccess: () => {
      setReplyingTo(null)
      setReplyText('')
      queryClient.invalidateQueries({ queryKey: ['forum-topic', id] })
      toast({ title: t('forum.replyAdded') })
    },
    onError: (err: any) => toast({ title: err.response?.data?.error || t('common.error'), variant: 'destructive' })
  })

  if (!topic) {
    return <div className="max-w-3xl mx-auto px-4 py-12 text-center text-muted-foreground">{t('forum.loading')}</div>
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-6">
      <div className="flex gap-4 mb-6">
        <div className="flex flex-col items-center gap-1">
          <button type="button" aria-label={t('forum.upvote')} onClick={() => voteMutation.mutate('up')} className={`p-1 rounded hover:bg-secondary ${topic.userVote === 'up' ? 'text-green-500' : 'text-muted-foreground'}`}>
            <ArrowBigUp className="w-8 h-8" />
          </button>
          <span className="text-lg font-bold">{topic.votes}</span>
          <button type="button" aria-label={t('forum.downvote')} onClick={() => voteMutation.mutate('down')} className={`p-1 rounded hover:bg-secondary ${topic.userVote === 'down' ? 'text-red-500' : 'text-muted-foreground'}`}>
            <ArrowBigDown className="w-8 h-8" />
          </button>
        </div>
        <div className="flex-1">
          <h1 className="font-display text-2xl font-bold">{topic.title}</h1>
          <div className="flex items-center gap-2 mt-2 text-sm text-muted-foreground">
            <User className="w-4 h-4" />
            <Link to={`/profile/${topic.author}`} className="hover:text-primary transition-colors">{topic.author}</Link>
            <span>{formatDistanceToNow(topic.createdAt)}</span>
          </div>
          <p className="mt-4 whitespace-pre-wrap">{topic.content}</p>
        </div>
      </div>

      <div className="border-t border-border pt-6">
        <h2 className="font-semibold mb-4 flex items-center gap-2">
          <MessageCircle className="w-5 h-5" /> {t('forum.comments')} ({topic.comments?.length || 0})
        </h2>

        {isAuthenticated && (
          <div className="flex gap-2 mb-6">
            <input
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && commentMutation.mutate()}
              placeholder={t('forum.commentPlaceholder')}
              className="flex-1 px-4 py-2 rounded-lg bg-secondary border border-border focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
            <Button size="icon" onClick={() => commentMutation.mutate()} disabled={!comment.trim()}>
              <Send className="w-4 h-4" />
            </Button>
          </div>
        )}

        <div className="space-y-4">
          {topic.comments?.map((c: any) => (
            <motion.div key={c._id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="p-4 rounded-xl bg-card border border-border">
              <div className="flex items-center gap-2 mb-2 text-sm text-muted-foreground">
                <User className="w-4 h-4" />
                <Link to={`/profile/${c.author}`} className="hover:text-primary transition-colors">{c.author}</Link>
                <span>{formatDistanceToNow(c.createdAt)}</span>
              </div>
              <p className="text-sm">{c.content}</p>

              {isAuthenticated && (
                <div className="flex items-center gap-3 mt-3">
                  <button
                    type="button"
                    onClick={() => likeMutation.mutate(c._id)}
                    className={`flex items-center gap-1 text-xs hover:text-primary ${c.liked ? 'text-red-500' : 'text-muted-foreground'}`}
                    aria-label={t('forum.commentLike')}
                    title={t('forum.commentLike')}
                  >
                    <Heart className={`w-4 h-4 ${c.liked ? 'fill-current' : ''}`} />
                    <span>{c.likes || 0}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setReplyingTo(replyingTo === c._id ? null : c._id)
                      setReplyText('')
                    }}
                    className="flex items-center gap-1 text-xs text-muted-foreground hover:text-primary"
                    aria-label={t('forum.commentReply')}
                    title={t('forum.commentReply')}
                  >
                    <MessageSquare className="w-4 h-4" />
                    <span>{t('forum.commentReply')}</span>
                  </button>
                </div>
              )}

              {replyingTo === c._id && isAuthenticated && (
                <div className="flex gap-2 mt-3">
                  <input
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && replyText.trim() && replyMutation.mutate({ commentId: c._id, content: replyText })}
                    placeholder={t('forum.writeReply')}
                    className="flex-1 px-3 py-1.5 text-sm rounded-lg bg-secondary border border-border focus:outline-none focus:ring-2 focus:ring-primary/50"
                  />
                  <Button size="icon" onClick={() => replyMutation.mutate({ commentId: c._id, content: replyText })} disabled={!replyText.trim()}>
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              )}

              {c.replies?.length > 0 && (
                <div className="mt-3 pl-4 border-l-2 border-border">
                  <p className="text-xs font-semibold text-muted-foreground mb-2">{t('forum.replies')}</p>
                  <div className="space-y-2">
                    {c.replies.map((reply: any, idx: number) => (
                      <div key={idx} className="text-sm">
                        <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                          <User className="w-3 h-3" />
                          <Link to={`/profile/${reply.author}`} className="hover:text-primary transition-colors">{reply.author}</Link>
                          <span>{formatDistanceToNow(reply.createdAt)}</span>
                        </div>
                        <p className="text-sm text-foreground">{reply.content}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  )
}
